import { useCallback, useMemo } from "react";
import { hasAnyDemandDownload, NOTIFICATION_TYPE } from "@demandproai/shared";
import { useNotificationContext } from "../contexts/NotificationContext";
import useSuggestedEditsFeatureFlag from "./useSuggestedEditsFeatureFlag";
import useCertifiedLifecareCompany from "./useCertifiedLifecareCompany";
import useResultProgress from "../components/customHook/useResultProgress";

const EDIT_TOOLTIP = {
  disabled: "Available once case setup finishes",
  enabled: "Add or change documents and other info related to the case",
};

const resolveIsDownloadAvailable = (caseData) => {
  if (!caseData) {
    return { isDownloadAvailable: false, isDeleteDisabled: false };;
  }

  const { isDraftCase, processCount } = caseData;
  if (isDraftCase) {
    return { isDownloadAvailable: false, isDeleteDisabled: false };
  }

  if (processCount === 0) {
    return { isDownloadAvailable: false, isDeleteDisabled: true };
  }
  return { isDownloadAvailable: processCount >= 1, isDeleteDisabled: false };
};

const useCaseActionControls = (caseData) => {
  const { markCaseAsRead } = useNotificationContext();
  const isSuggestedEditsUiEnabled = useSuggestedEditsFeatureFlag();
  const isCertifiedLifecareCompany = useCertifiedLifecareCompany();
  const { isDeleteDisabled, isDownloadAvailable } = useMemo(
    () => resolveIsDownloadAvailable(caseData),
    [caseData]
  );

  // the virtual user is still populating the case, so editing now shows empty or shifting info
  const { isVirtualUserRunning } = useResultProgress(caseData?.createRequests, caseData?.isDraftCase);
  const editTooltip = isVirtualUserRunning ? EDIT_TOOLTIP.disabled : EDIT_TOOLTIP.enabled;

  const showDemandStudioButton = useMemo(() => {
    if (typeof caseData?.hasDownloadedDemand === "boolean") {
      return caseData.hasDownloadedDemand;
    }
    return hasAnyDemandDownload(caseData?.docxFiles);
  }, [caseData?.hasDownloadedDemand, caseData?.docxFiles]);

  const markAsRead = useCallback(
    (notificationType) => markCaseAsRead(caseData?._id, notificationType),
    [markCaseAsRead, caseData?._id]
  );

  const onMarkDemandReady = useCallback(
    () => markAsRead(NOTIFICATION_TYPE.DEMAND_READY),
    [markAsRead]
  );

  const onMarkNoteAdded = useCallback(
    () => markAsRead(NOTIFICATION_TYPE.NOTE_ADDED),
    [markAsRead]
  );

  return {
    isDownloadAvailable,
    isDeleteDisabled,
    isEditDisabled: isVirtualUserRunning,
    editTooltip,
    showDemandStudioButton,
    isSuggestedEditsUiEnabled,
    isCertifiedLifecareCompany,
    onMarkDemandReady,
    onMarkNoteAdded,
  };
};

export default useCaseActionControls;
